import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
interface CounterProps {
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
}
function Counter({
  end,
  duration = 2000,
  suffix = '',
  prefix = ''
}: CounterProps) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number | null = null;
    const startValue = 0;
    const animate = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(startValue + (end - startValue) * easeOutQuart);
      setCount(current);
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);
  return <span ref={ref} data-id="element-526">
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>;
}
const stats = [{
  value: <Counter end={13} suffix="+" data-id="element-527" />,
  label: 'Years experience'
}, {
  value: <Counter end={140} suffix="+" data-id="element-528" />,
  label: 'Years combined industry experience'
}, {
  value: <Counter end={3000} suffix="+" data-id="element-529" />,
  label: 'Jobs completed'
}, {
  value: <Counter end={15000} suffix="+" data-id="element-530" />,
  label: 'Air conditioning units serviced'
}, {
  value: <Counter end={1000} suffix="+" data-id="element-531" />,
  label: 'Air conditioning units installed'
}, {
  value: 'Top 10%',
  label: 'AccuTemp are in the top 10% of HVAC firms that hold Constructionline Gold'
}, {
  value: <Counter end={1400} prefix="£" data-id="element-532" />,
  label: 'Starting installation fee + VAT'
}];
const trustLines = ['Manufacturer-level support. In-house. Always.', 'Installations built to last 16 years.', 'Trusted by FM companies, landlords, and M&E contractors.'];
export function Stats() {
  return <section className="py-16 px-4 md:px-6" data-id="element-533">
      <div className="w-[95%] mx-auto bg-[#F7F5F0] rounded-3xl px-8 py-14 md:px-16 md:py-20 lg:px-20" data-id="element-534">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mb-16" data-id="element-535">
          <p className="text-xl md:text-2xl leading-relaxed text-primary-dark font-serif" data-id="element-536">
            The UK's trusted commercial air conditioning specialist. Daikin D1+
            Premier Partners, headquartered in Coventry with nationwide
            coverage.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 mb-16" data-id="element-537">
          {stats.map((stat, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.08
        }} className="border-t border-primary-dark/15 pt-6" data-id="element-538">
              <div className="text-4xl sm:text-5xl md:text-6xl font-medium mb-3 text-primary-dark leading-none" data-id="element-539">
                {stat.value}
              </div>
              <div className="text-xs md:text-sm text-primary-dark/70 uppercase tracking-wider leading-snug" data-id="element-540">
                {stat.label}
              </div>
            </motion.div>)}
        </div>

        {/* Trust lines */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-10 border-t border-primary-dark/15" data-id="element-541">
          {trustLines.map((line, index) => <div key={index} className="text-sm text-primary-dark/70 leading-relaxed text-center md:text-left" data-id="element-542">
              {line}
            </div>)}
        </motion.div>
      </div>
    </section>;
}